import { Amplify, Auth, API, Storage } from "aws-amplify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import duration from "dayjs/plugin/duration";
import advancedFormat from "dayjs/plugin/advancedFormat";
import CryptoJS from "crypto-js";
import mixpanel from "mixpanel-browser";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";

import {
  APIRoutes,
  amplifyConfigData,
  amplifyConfigDataNewSetUp,
  amplifyGoConfigData,
  amplifySSOConfigData,
  amplifyZpaxSSOConfigData,
  amplifyMicrosoftSSOConfigData,
  apiName,
  apiNameCDL,
  apiNameCDLDev,
  apiOrigin,
} from "./config";

import { APIProviderFactory } from "./hexagon/adapters/driven/providers/apiProvider";
import {
  AuthServiceImplFactory,
  setAuthProviderForAutoRefresh,
  setAmplifyProviderForAutoRefresh,
  setSsoRepository,
} from "./hexagon/core/impl/authServiceImpl";
import { AuthProviderFactory } from "./hexagon/adapters/driven/providers/authProvider";
import { BillingAdminRepositoryFactory } from "./hexagon/adapters/driven/repositories/billingAdminRepository";
import { BillingAdminServiceImplFactory } from "./hexagon/core/impl/billingAdminServiceImpl";
import { BillingAdminControllerFactory } from "./hexagon/adapters/driver/billingAdminController";
import { BillingModuleRepositoryFactory } from "./hexagon/adapters/driven/repositories/billingModuleRepository";
import { billingModuleServiceImplFactory } from "./hexagon/core/impl/billingModuleServiceImpl";
import { BillingModuleControllerFactory } from "./hexagon/adapters/driver/billingModuleController";
import { ProductUtilizationRepositoryFactory } from "./hexagon/adapters/driven/repositories/productUtilizationRepository";
import { productUtilizationServiceImplFactory } from "./hexagon/core/impl/productUtilizationServiceImpl";
import { ProductUtilizationControllerFactory } from "./hexagon/adapters/driver/productUtilizationController";
import { NpProductivityTimelineRepositoryFactory } from "./hexagon/adapters/driven/repositories/npProductivityTimelineRepository";
import { npProductivityTimelineServiceImplFactory } from "./hexagon/core/impl/npProductivityTimelineServiceImpl";
import { NpProductivityTimelineControllerFactory } from "./hexagon/adapters/driver/npProductivityTimelineController";
import { DeviceOrdersRepositoryFactory } from "./hexagon/adapters/driven/repositories/deviceOrdersRepository";
import { deviceOrdersServiceImplFactory } from "./hexagon/core/impl/deviceOrdersServiceImpl";
import { DeviceOrdersControllerFactory } from "./hexagon/adapters/driver/deviceOrdersController";
import { ClinicRepositoryFactory } from "./hexagon/adapters/driven/repositories/clinicRepository";
import { ClinicServiceImplFactory } from "./hexagon/core/impl/clinicServiceImpl";
import { LocalStorageProviderFactory } from "./hexagon/adapters/driven/providers/localStorageProvider";
import { ClinicControllerFactory } from "./hexagon/adapters/driver/clinicController";
import { CompassusDashboardRepositoryFactory } from "./hexagon/adapters/driven/repositories/compassusDashboardRepository";
import { CompassusDashboardServiceImplFactory } from "./hexagon/core/impl/compassusDashboardServiceImpl";
import { CompassusDashboardControllerFactory } from "./hexagon/adapters/driver/compassusDashboardController";
import { AlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/alertRepository";
import { AlertControllerFactory } from "./hexagon/adapters/driver/alertController";
import { AlertServiceImplFactory } from "./hexagon/core/impl/alertServiceImpl";
import { ExternalStorageProviderFactory } from "./hexagon/adapters/driven/providers/externalStorageProvider";
import { UserSignatureProviderFactory } from "./hexagon/adapters/driven/providers/userSignatureProvider";
import { UserSignatureServiceImpl } from "./hexagon/core/impl/userSignatureServiceImpl";
import { UserSignatureControllerFactory } from "./hexagon/adapters/driver/userSignatureController";
import { AuthControllerFactory } from "./hexagon/adapters/driver/authController";
import { TimeProviderFactory } from "./hexagon/adapters/driven/providers/timeProvider";
import { AmplifyProviderFactory } from "./hexagon/adapters/driven/providers/amplifyProvider";
import { JSONUtilFactory } from "./hexagon/adapters/driven/providers/jsonUtilProvider";
import { CryptoProviderFactory } from "./hexagon/adapters/driven/providers/cryptoFactory";
import { CircadiaEngineProviderFactory } from "./hexagon/adapters/driven/providers/circadiaEngineProvider";
import { PsnAlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/psnAlertRepository";
import { PsnAlertServiceImplFactory } from "./hexagon/core/impl/psnAlertServiceImpl";
import { PsnAlertControllerFactory } from "./hexagon/adapters/driver/psnAlertController";
import { SupportControllerFactory } from "./hexagon/adapters/driver/supportController";
import { SupportRepositoryFactory } from "./hexagon/adapters/driven/repositories/supportRepository";
import { SupportServiceImplFactory } from "./hexagon/core/impl/supportServiceImpl";
import { NeoWorkflowRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoWorkflowRepository";
import { NeoWorkflowServiceImplFactory } from "./hexagon/core/impl/neoWorkflowServiceImpl";
import { NeoWorkflowControllerFactory } from "./hexagon/adapters/driver/neoWorkflowController";
import { NeoProjectRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoProjectRepository";
import { NeoProjectServiceImplFactory } from "./hexagon/core/impl/neoProjectServiceImpl";
import { NeoProjectControllerFactory } from "./hexagon/adapters/driver/neoProjectController";
import { NeoConversationRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoConversationRepository";
import { NeoConversationServiceImplFactory } from "./hexagon/core/impl/neoConversationServiceImpl";
import { NeoConversationControllerFactory } from "./hexagon/adapters/driver/neoConversationController";
import { NeoDashboardRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoDashboardRepository";
import { NeoDashboardServiceImplFactory } from "./hexagon/core/impl/neoDashboardServiceImpl";
import { NeoDashboardControllerFactory } from "./hexagon/adapters/driver/neoDashboardController";
import { NeoTicketRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoTicketRepository";
import { NeoTicketServiceImplFactory } from "./hexagon/core/impl/neoTicketServiceImpl";
import { NeoTicketControllerFactory } from "./hexagon/adapters/driver/neoTicketController";
import { NeoActionExecutionRepositoryFactory } from "./hexagon/adapters/driven/repositories/neoActionExecutionRepository";
import { NeoActionExecutionServiceImplFactory } from "./hexagon/core/impl/neoActionExecutionServiceImpl";
import { NeoActionExecutionControllerFactory } from "./hexagon/adapters/driver/neoActionExecutionController";
import { PushNotificationProviderFactory } from "./hexagon/adapters/driven/providers/pushNotificationProvider";
import { ReportRepositoryFactory } from "./hexagon/adapters/driven/repositories/reportRepository";
import { ReportServiceImplFactory } from "./hexagon/core/impl/reportServiceImpl";
import { ReportControllerFactory } from "./hexagon/adapters/driver/reportController";
import { PatientRepositoryFactory } from "./hexagon/adapters/driven/repositories/patientRepository";
import { PatientServiceImplFactory } from "./hexagon/core/impl/patientServiceImpl";
import { PatientControllerFactory } from "./hexagon/adapters/driver/patientController";
import { MedicationReconciliationRepositoryFactory } from "./hexagon/adapters/driven/repositories/medicationReconciliationRepository";
import { MedicationReconciliationServiceFactory } from "./hexagon/core/impl/medicationReconciliationServiceImpl";
import { MedicationReconciliationControllerFactory } from "./hexagon/adapters/driver/medicationReconciliationController";
import { NotificationRepositoryFactory } from "./hexagon/adapters/driven/repositories/notificationRepository";
import { NotificationServiceImplFactory } from "./hexagon/core/impl/notificationServiceImpl";
import { NotificationControllerFactory } from "./hexagon/adapters/driver/notificationController";
import { SearchControllerFactory } from "./hexagon/adapters/driver/searchController";
import { SearchServiceImplFactory } from "./hexagon/core/impl/searchServiceImpl";
import { SearchRepositoryFactory } from "./hexagon/adapters/driven/repositories/searchRepository";
import { WatchlistRepositoryFactory } from "./hexagon/adapters/driven/repositories/watchlistRepository";
import { watchlistServiceImplFactory } from "./hexagon/core/impl/watchlistServiceImpl";
import { WatchlistControllerFactory } from "./hexagon/adapters/driver/watchlistController";
import { RespRateRepositoryFactory } from "./hexagon/adapters/driven/repositories/respRateRepository";
import { RespRateServiceImplFactory } from "./hexagon/core/impl/respRateServiceImpl";
import { RespRateControllerFactory } from "./hexagon/adapters/driver/respRateController";
import { OfflineDeviceControllerFactory } from "./hexagon/adapters/driver/offlineDeviceController";
import { OfflineDeviceRepositoryFactory } from "./hexagon/adapters/driven/repositories/offlineDeviceRepository";
import { offlineDeviceServiceImplFactory } from "./hexagon/core/impl/offlineDeviceServiceImpl";
import { ConsentRepositoryFactory } from "./hexagon/adapters/driven/repositories/consentRepository";
import { consentServiceImplFactory } from "./hexagon/core/impl/consentServiceImpl";
import { ConsentControllerFactory } from "./hexagon/adapters/driver/consentController";
import { AnalyticsProviderFactory } from "./hexagon/adapters/driven/providers/AnalyticsProvider";
import { AnalyticsControllerFactory } from "./hexagon/adapters/driver/analyticsController";
import { CDLServiceImplFactory } from "./hexagon/core/impl/cdlServiceImpl";
import { CDLRepositoryFactory } from "./hexagon/adapters/driven/repositories/cdlRepository";
import { BedExitsControllerFactory } from "./hexagon/adapters/driver/bedExitsController";
import { BedExitsServiceImplFactory } from "./hexagon/core/impl/bedExitsServiceImpl";
import { BedExitsRepositoryFactory } from "./hexagon/adapters/driven/repositories/bedExitsRepository";
import { UserControllerFactory } from "./hexagon/adapters/driver/userController";
import { UserServiceImplFactory } from "./hexagon/core/impl/userServiceImpl";
import { UserRepositoryFactory } from "./hexagon/adapters/driven/repositories/userRepository";
import { webIntercomProviderFactory } from "./hexagon/adapters/driven/providers/webIntercomProvider";
import { Observable } from "./util/observable";
import { TrackingRepositoryFactory } from "./hexagon/adapters/driven/repositories/trackingRepository";
import { TrackingServiceImplFactory } from "./hexagon/core/impl/trackingServiceImpl";
import { TrackingControllerFactory } from "./hexagon/adapters/driver/trackingController";
import { SummaryRepositoryFactory } from "./hexagon/adapters/driven/repositories/summaryRepository";
import { SummaryServiceImplFactory } from "./hexagon/core/impl/summaryServiceImpl";
import { SummaryControllerFactory } from "./hexagon/adapters/driver/summaryController";
import { ElectronEngineProviderFactory } from "./hexagon/adapters/driven/providers/electronEngineProvider";
import { InsightsRepositoryFactory } from "./hexagon/adapters/driven/repositories/insightsRepository";
import { insightsServiceImplFactory } from "./hexagon/core/impl/insightsServiceImpl";
import { InsightsControllerFactory } from "./hexagon/adapters/driver/insightsController";
import { IPVRepositoryFactory } from "./hexagon/adapters/driven/repositories/ipvRepository";
import { ipvServiceImplFactory } from "./hexagon/core/impl/ipvServiceImpl";
import { IPVControllerFactory } from "./hexagon/adapters/driver/ipvController";
import { HomeHealthControllerFactory } from "./hexagon/adapters/driver/homeHealthController";
import { HomeHealthServiceImplFactory } from "./hexagon/core/impl/homeHealthServiceImpl";
import { HomeHealthRepositoryFactory } from "./hexagon/adapters/driven/repositories/homeHealthRepository";
import { SalesRepositoryFactory } from "./hexagon/adapters/driven/repositories/salesRepository";
import { SalesServiceImplFactory } from "./hexagon/core/impl/salesServiceImpl";
import { SalesControllerFactory } from "./hexagon/adapters/driver/salesController";
import { RTBloggerRepositoryFactory } from "./hexagon/adapters/driven/repositories/rtBloggerRepository";
import { rtBloggerServiceImplFactory } from "./hexagon/core/impl/rtBloggerServiceImpl";
import { RTBloggerControllerFactory } from "./hexagon/adapters/driver/rtBloggerController";
import { VitalSyncRepositoryFactory } from "./hexagon/adapters/driven/repositories/vitalSyncRepository";
import { VitalSyncServiceImplFactory } from "./hexagon/core/impl/vitalSyncServiceImpl";
import { VitalSyncControllerFactory } from "./hexagon/adapters/driver/vitalSyncController";
import { SMSRepositoryFactory } from "./hexagon/adapters/driven/repositories/smsRepository";
import { SMSServiceImplFactory } from "./hexagon/core/impl/smsServiceImpl";
import { SMSControllerFactory } from "./hexagon/adapters/driver/smsController";
import { AppPerformanceSurveyRepositoryFactory } from "./hexagon/adapters/driven/repositories/appPerformanceSurveyRepository";
import { AppPerformanceSurveyServiceImplFactory } from "./hexagon/core/impl/appPerformanceSurveyServiceImpl";
import { AppPerformanceSurveyControllerFactory } from "./hexagon/adapters/driver/appPerformanceSurveyController";
import {
  createUsaddressWorker,
  UsaddressProviderFactory,
} from "./hexagon/adapters/driven/providers/usaddressProvider";
import { AddressValidationServiceImplFactory } from "./hexagon/core/impl/addressValidationServiceImpl";
import { AddressValidationControllerFactory } from "./hexagon/adapters/driver/addressValidationController";
import { FacilityRepositoryFactory } from "./hexagon/adapters/driven/repositories/facilityRepository";
import { FacilityServiceImplFactory } from "./hexagon/core/impl/facilityServiceImpl";
import { FacilityControllerFactory } from "./hexagon/adapters/driver/facilityController";

import { HealthScribeWebSocketProvider } from "./hexagon/adapters/driven/providers/healthScribeWebSocketProvider";
import { HealthScribeAudioProvider } from "./hexagon/adapters/driven/providers/healthScribeAudioProvider";
import { HealthScribeRepositoryFactory } from "./hexagon/adapters/driven/repositories/healthScribeRepository";
import { HealthScribeServiceFactory } from "./hexagon/core/impl/healthScribeServiceImpl";
import { HealthScribeControllerFactory } from "./hexagon/adapters/driver/healthScribeController";

import { NavigatorRepositoryFactory } from "./hexagon/adapters/driven/repositories/navigatorRepository";
import { NavigatorServiceImplFactory } from "./hexagon/core/impl/navigatorServiceImpl";
import { NavigatorControllerFactory } from "./hexagon/adapters/driver/navigatorController";

import { CompassusNavigatorRepositoryFactory } from "./hexagon/adapters/driven/repositories/compassusNavigatorRepository";
import { CompassusNavigatorServiceImplFactory } from "./hexagon/core/impl/compassusNavigatorServiceImpl";
import { CompassusNavigatorControllerFactory } from "./hexagon/adapters/driver/compassusNavigatorController";
import { CompassusCarePlanRepositoryFactory } from "./hexagon/adapters/driven/repositories/compassusCarePlanRepository";
import { CompassusCarePlanServiceImplFactory } from "./hexagon/core/impl/compassusCarePlanServiceImpl";
import { CompassusCarePlanControllerFactory } from "./hexagon/adapters/driver/compassusCarePlanController";

import { HPITemplateRepositoryFactory } from "./hexagon/adapters/driven/repositories/hpiTemplateRepository";
import { HPITemplateServiceImplFactory } from "./hexagon/core/impl/hpiTemplateServiceImpl";
import { HPITemplateControllerFactory } from "./hexagon/adapters/driver/hpiTemplateController";

import { EngagementTrackerProviderFactory } from "./hexagon/adapters/driven/providers/engagementTrackerProvider";

import { VisitNotesRepositoryFactory } from "./hexagon/adapters/driven/repositories/VisitNotesRepository";
import { VisitNotesServiceImplFactory } from "./hexagon/core/impl/VisitNotesServiceImpl";
import { VisitNotesControllerFactory } from "./hexagon/adapters/driver/VisitNotesController";
import { AfterVisitSummaryRepositoryFactory } from "./hexagon/adapters/driven/repositories/afterVisitSummaryRepository";
import { AfterVisitSummaryServiceImplFactory } from "./hexagon/core/impl/afterVisitSummaryServiceImpl";
import { AfterVisitSummaryControllerFactory } from "./hexagon/adapters/driver/afterVisitSummaryController";
import { SsoRepositoryFactory } from "./hexagon/adapters/driven/repositories/ssoRepository";
import { EhrRecordsRepositoryFactory } from "./hexagon/adapters/driven/repositories/ehrRecordsRepository";
import { EhrRecordsServiceImplFactory } from "./hexagon/core/impl/ehrRecordsServiceImpl";
import { EhrRecordsControllerFactory } from "./hexagon/adapters/driver/ehrRecordsController";
import { ColumnPreferencesRepositoryFactory } from "./hexagon/adapters/driven/repositories/columnPreferencesRepository";
import { ColumnPreferencesServiceImplFactory } from "./hexagon/core/impl/columnPreferencesServiceImpl";
import { ColumnPreferencesControllerFactory } from "./hexagon/adapters/driver/columnPreferencesController";
import { FeatureTourRepositoryFactory } from "./hexagon/adapters/driven/repositories/featureTourRepository";
import { FeatureTourServiceImplFactory } from "./hexagon/core/impl/featureTourServiceImpl";
import { FeatureTourControllerFactory } from "./hexagon/adapters/driver/featureTourController";
import { CoordNotesRepositoryFactory } from "./hexagon/adapters/driven/repositories/coordNotesRepository";
import { CoordNotesServiceImplFactory } from "./hexagon/core/impl/coordNotesServiceImpl";
import { CoordNotesControllerFactory } from "./hexagon/adapters/driver/coordNotesController";
import { HchbCoordinationNotesRepositoryFactory } from "./hexagon/adapters/driven/repositories/hchbCoordinationNotesRepository";
import { HchbCoordinationNotesServiceImplFactory } from "./hexagon/core/impl/hchbCoordinationNotesServiceImpl";
import { HchbCoordinationNotesControllerFactory } from "./hexagon/adapters/driver/hchbCoordinationNotesController";
import { TelehealthRepositoryFactory } from "./hexagon/adapters/driven/repositories/telehealthRepository";
import { TelehealthServiceImplFactory } from "./hexagon/core/impl/telehealthServiceImpl";
import { TelehealthControllerFactory } from "./hexagon/adapters/driver/telehealthController";

export const amplifyProvider = AmplifyProviderFactory({
  amplify: Amplify,
  auth: Auth,
  amplifyConfig: {
    amplifyConfigData,
    amplifyConfigDataNewSetUp,
    amplifyGoConfigData,
    amplifySSOConfigData,
    amplifyZpaxSSOConfigData,
    amplifyMicrosoftSSOConfigData,
  },
});
amplifyProvider.amplifyConfigure();

export const cryptoJsProvider = (secretKey: string) =>
  CryptoProviderFactory({
    cryptoJS: CryptoJS,
    secretKey: secretKey,
  });

export const circadiaEngineProvider = CircadiaEngineProviderFactory({
  circadia: window.circadia,
});

export const electronEngineProvider = ElectronEngineProviderFactory({
  electron: window.electron,
});

export const webIntercomProvider = webIntercomProviderFactory({
  globWindow: window,
});

export const analyticsProvider = AnalyticsProviderFactory(mixpanel);

export const analyticsController = AnalyticsControllerFactory({
  analyticsProvider: analyticsProvider,
  circadiaEngineProvider: circadiaEngineProvider,
});

export const timeProvider = TimeProviderFactory({
  dateLibrary: dayjs,
  utcPlugin: utc,
  timezonePlugin: timezone,
  isBetween,
  advancedFormat,
  relativeTime,
  duration,
  isToday,
});

export const AuthObserver = new Observable({ type: "initial", data: null });
if (
  circadiaEngineProvider.isWindowCircadia() &&
  circadiaEngineProvider.intfv >= 5
  // && circadiaEngineProvider.isIOS
) {
  if (circadiaEngineProvider.setCognitoLoginDoneCb)
    circadiaEngineProvider.setCognitoLoginDoneCb(() => {
      AuthObserver.update({ type: "login", payload: { success: true } });
    });

  if (circadiaEngineProvider.setCognitoLoginFailedCb)
    circadiaEngineProvider.setCognitoLoginFailedCb((error) => {
      AuthObserver.update({
        type: "login",
        payload: { success: false, error: error?.Msg },
      });
    });

  if (circadiaEngineProvider.setCognitoMfaRequiredCb)
    circadiaEngineProvider.setCognitoMfaRequiredCb((props) => {
      AuthObserver.update({
        type: "mfaRequired",
        payload: { success: false, props: props?.CODE_DELIVERY_DESTINATION },
      });
    });

  if (circadiaEngineProvider.setCognitoPinRequiredCb)
    circadiaEngineProvider.setCognitoPinRequiredCb(() => {
      AuthObserver.update({ type: "pinRequired", payload: null });
    });

  if (circadiaEngineProvider.setCognitoNewPasswordRequiredCb)
    circadiaEngineProvider.setCognitoNewPasswordRequiredCb((attrs) => {
      AuthObserver.update({ type: "newPasswordRequired", payload: attrs });
    });

  if (circadiaEngineProvider.setCognitoEmailOTPRequiredCb)
    circadiaEngineProvider.setCognitoEmailOTPRequiredCb(() => {
      AuthObserver.update({ type: "passwordlessOTPRequired", payload: null });
    });
}

const authProvider = AuthProviderFactory({
  Auth: amplifyProvider.auth,
  CircadiaEngine: circadiaEngineProvider,
  ElectronEngine: electronEngineProvider,
});

export const AuthService = AuthServiceImplFactory({
  authProvider,
  circadiaEngineProvider,
  electronEngineProvider,
});

export const APIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName,
});

export const CDLAPIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName: apiNameCDL,
});

export const CDLDevAPIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName: apiNameCDLDev,
});

// SSO Repository - wired after APIProvider to break circular dependency
const ssoRepository = SsoRepositoryFactory({
  APIProvider,
  APIRoutes,
});

// Initialize SSO repository in AuthService for token refresh
setSsoRepository(ssoRepository);

// Initialize auth provider reference for auto-refresh
setAuthProviderForAutoRefresh(authProvider);

// Initialize amplify provider reference for SSO config switching during refresh
setAmplifyProviderForAutoRefresh(amplifyProvider);

// Billing Admin
const billingAdminRepository = BillingAdminRepositoryFactory({
  APIRoutes,
  APIProvider,
});

const billingAdminService = BillingAdminServiceImplFactory({
  billingAdminRepository,
});

export const billingAdminController = BillingAdminControllerFactory({
  billingAdminService,
});
// End Billing Admin

// Billing Module (legacy "billingNew" port)
const billingModuleRepository = BillingModuleRepositoryFactory({
  APIRoutes,
  APIProvider,
});

const billingModuleService = billingModuleServiceImplFactory({
  billingModuleRepository,
});

export const billingModuleController = BillingModuleControllerFactory({
  billingModuleService,
});
// End Billing Module

// Product Utilization dashboard (superadmin-only)
const productUtilizationRepository = ProductUtilizationRepositoryFactory({
  APIRoutes,
  APIProvider,
});

const productUtilizationService = productUtilizationServiceImplFactory({
  productUtilizationRepository,
});

export const productUtilizationController = ProductUtilizationControllerFactory(
  {
    productUtilizationService,
  }
);
// End Product Utilization

// NP Productivity Timeline (superadmin-only)
const npProductivityTimelineRepository =
  NpProductivityTimelineRepositoryFactory({
    APIRoutes,
    APIProvider,
  });

const npProductivityTimelineService = npProductivityTimelineServiceImplFactory({
  npProductivityTimelineRepository,
});

export const npProductivityTimelineController =
  NpProductivityTimelineControllerFactory({
    npProductivityTimelineService,
  });
// End NP Productivity Timeline

// Device Orders
const deviceOrdersRepository = DeviceOrdersRepositoryFactory({
  APIRoutes,
  APIProvider,
});

const deviceOrdersService = deviceOrdersServiceImplFactory({
  deviceOrdersRepository,
});

export const deviceOrdersController = DeviceOrdersControllerFactory({
  deviceOrdersService,
});
// End Device Orders

export const jsonUtil = JSONUtilFactory({ JSON });
export const localStorageProvider = LocalStorageProviderFactory({
  localStorage: localStorage,
});

export const engagementTrackerProvider = EngagementTrackerProviderFactory({
  localStorageProvider,
});

export const pushNotificationProvider = PushNotificationProviderFactory({
  circadiaEngineProvider,
  APIProvider,
  apiRoute: APIRoutes.apiStoreToken,
  AuthService,
});

export const externalStorageProvider = ExternalStorageProviderFactory({
  APIProvider,
  APIPath: APIRoutes.apiGetFileFromS3,
  AuthService,
});

// User signature (Chart Review re-usable e-sign)
//
// `userSignatureProvider` writes to `resident-face-sheet/public/
// signatures/{file}` via Amplify Storage. The service pairs each
// upload with a `Auth.updateUserAttributes({ 'custom:signFileName':
// fileName })` write so the next modal open auto-loads the signature
// instead of prompting the BP to re-draw — see legacy
// `AssessmentSignature.js`.
const userSignatureProvider = UserSignatureProviderFactory({ Storage });

const userSignatureService = UserSignatureServiceImpl({
  userSignatureProvider,
  authProvider: { updateUserAttributes: authProvider.updateUserAttributes },
});

export const userSignatureController = UserSignatureControllerFactory({
  userSignatureService,
});

// Notification
const notificationRepository = NotificationRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const notificationService = NotificationServiceImplFactory({
  notificationRepository,
});

export const notificationController = NotificationControllerFactory({
  notificationService,
});

// Clinic
const clinicRepository = ClinicRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const clinicService = ClinicServiceImplFactory({
  clinicRepository,
  localStorageProvider,
  notificationService,
});
//

// Compassus Productivity Dashboard
const compassusDashboardRepository = CompassusDashboardRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});
const compassusDashboardService = CompassusDashboardServiceImplFactory({
  compassusDashboardRepository,
});

// Alert
const alertRepository = AlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const alertService = AlertServiceImplFactory({
  alertRepository,
  externalStorageProvider,
});
//

// PSN Alert
const psnAlertRepository = PsnAlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const psnAlertService = PsnAlertServiceImplFactory({
  psnAlertRepository,
});
//

// Patient
const patientRepository = PatientRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const patientService = PatientServiceImplFactory({
  patientRepository,
});

//Report
const reportRepository = ReportRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const reportService = ReportServiceImplFactory({
  reportRepository,
});

const cdlRepository = CDLRepositoryFactory({
  APIProvider: CDLAPIProvider,
  APIProvider2: CDLDevAPIProvider,
  AuthService,
  APIRoutes,
  timeProvider,
});

const cdlService = CDLServiceImplFactory({ cdlRepository });

export const patientController = PatientControllerFactory({
  patientService,
  timeProvider,
  cdlService,
});

// MedicationReconciliation
const medicationReconciliationRepository =
  MedicationReconciliationRepositoryFactory({
    APIProvider,
    AuthService,
    APIRoutes,
  });
const medicationReconciliationService = MedicationReconciliationServiceFactory({
  medicationReconciliationRepository,
});
export const medicationReconciliationController =
  MedicationReconciliationControllerFactory({
    medicationReconciliationService,
  });
//

// Resprate
const respRateRepository = RespRateRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const respRateService = RespRateServiceImplFactory({
  respRateRepository,
});

export const respRateController = RespRateControllerFactory({
  respRateService,
});
//

// Watchlist
const watchlistRepository = WatchlistRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const watchlistService = watchlistServiceImplFactory({ watchlistRepository });

export const watchlistController = WatchlistControllerFactory({
  watchlistService,
});

// User
const userRepository = UserRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});
const userService = UserServiceImplFactory({ userRepository });

export const userController = UserControllerFactory({
  userService,
});

// BedExits
const bedExitsRepository = BedExitsRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});
const bedExitsService = BedExitsServiceImplFactory({ bedExitsRepository });

export const bedExitsController = BedExitsControllerFactory({
  bedExitsService,
});

// Offline device
const offlineDeviceRepository = OfflineDeviceRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const offlineDeviceService = offlineDeviceServiceImplFactory({
  offlineDeviceRepository,
});

export const offlineDeviceController = OfflineDeviceControllerFactory({
  offlineDeviceService,
});

// Insights
const insightsRepository = InsightsRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const insightsService = insightsServiceImplFactory({
  insightsRepository,
});

export const insightsController = InsightsControllerFactory({
  insightsService,
  timeProvider,
});

//ipv
const ipvRepository = IPVRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const ipvService = ipvServiceImplFactory({ ipvRepository });

export const ipvController = IPVControllerFactory({
  ipvService,
});

//consent
const consentRepository = ConsentRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const consentService = consentServiceImplFactory({ consentRepository });

export const consentController = ConsentControllerFactory({
  consentService,
});
//

export const reportController = ReportControllerFactory({
  reportService,
  timeProvider,
});
export const clinicController = ClinicControllerFactory({
  clinicService,
});
export const compassusDashboardController = CompassusDashboardControllerFactory(
  {
    compassusDashboardService,
  }
);
export const alertController = AlertControllerFactory({
  alertService,
  externalStorageProvider,
});
export const psnAlertController = PsnAlertControllerFactory({
  psnAlertService,
});

export const authController = AuthControllerFactory({
  AuthService,
  localStorageProvider,
  cryptoJsProvider,
  AuthObserver,
  circadiaEngineProvider,
  electronEngineProvider,
});

// Z-Pax

const supportRepository = SupportRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const supportService = SupportServiceImplFactory({
  supportRepository,
});

export const supportController = SupportControllerFactory({
  supportService,
});

// Neo Workflow Builder (canvas + embedded chat). New Neo surface area is
// wired through the hexagon (repository → service → controller) rather than
// the legacy direct-`APIProvider` pattern used by the rest of the Neo page.
const neoWorkflowRepository = NeoWorkflowRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoWorkflowService = NeoWorkflowServiceImplFactory({
  neoWorkflowRepository,
});

export const neoWorkflowController = NeoWorkflowControllerFactory({
  neoWorkflowService,
});

// Neo Projects (projects + agents + files + instructions + tasks +
// activity + sharing) and Neo Tickets. Like the workflow builder, these new
// Neo surfaces go through the hexagon rather than the legacy direct-API
// `neoApi.ts` pattern still used by the shipped Neo Conversations experience.
const neoProjectRepository = NeoProjectRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoProjectService = NeoProjectServiceImplFactory({
  neoProjectRepository,
});

export const neoProjectController = NeoProjectControllerFactory({
  neoProjectService,
});

const neoConversationRepository = NeoConversationRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoConversationService = NeoConversationServiceImplFactory({
  neoConversationRepository,
});

export const neoConversationController = NeoConversationControllerFactory({
  neoConversationService,
});

// Neo Dashboards — a dashboard is a project-owned display-styling JSON plus an
// ordered set of CSV files, each rendered as a panel (line/bar/pie/table).
const neoDashboardRepository = NeoDashboardRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoDashboardService = NeoDashboardServiceImplFactory({
  neoDashboardRepository,
});

export const neoDashboardController = NeoDashboardControllerFactory({
  neoDashboardService,
});

const neoTicketRepository = NeoTicketRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoTicketService = NeoTicketServiceImplFactory({
  neoTicketRepository,
});

export const neoTicketController = NeoTicketControllerFactory({
  neoTicketService,
});

const neoActionExecutionRepository = NeoActionExecutionRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const neoActionExecutionService = NeoActionExecutionServiceImplFactory({
  neoActionExecutionRepository,
});

export const neoActionExecutionController = NeoActionExecutionControllerFactory(
  {
    neoActionExecutionService,
  }
);

const searchRepository = SearchRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const searchService = SearchServiceImplFactory({
  searchRepository,
});
export const searchController = SearchControllerFactory({
  searchService,
});

const trackingRepository = TrackingRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const trackingService = TrackingServiceImplFactory({
  trackingRepository,
});

export const trackingController = TrackingControllerFactory({
  trackingService,
});

const summaryRepository = SummaryRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const summaryService = SummaryServiceImplFactory({
  summaryRepository,
});

export const summaryController = SummaryControllerFactory({
  summaryService,
});

// Home Health
const homeHealthRepository = HomeHealthRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const homeHealthService = HomeHealthServiceImplFactory({
  homeHealthRepository,
});

export const homeHealthController = HomeHealthControllerFactory({
  homeHealthService,
});

//sales
const salesRepository = SalesRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const salesService = SalesServiceImplFactory({ salesRepository });

export const salesController = SalesControllerFactory({
  salesService,
});

//rtblogger
const rtBloggerRepository = RTBloggerRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const rtBloggerService = rtBloggerServiceImplFactory({ rtBloggerRepository });

export const rtBloggerController = RTBloggerControllerFactory({
  rtBloggerService,
});

// VitalSync
const vitalSyncRepository = VitalSyncRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const vitalSyncService = VitalSyncServiceImplFactory({ vitalSyncRepository });

export const vitalSyncController = VitalSyncControllerFactory({
  vitalSyncService,
});

// SMS
const smsRepository = SMSRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const smsService = SMSServiceImplFactory({ smsRepository });

export const smsController = SMSControllerFactory({
  smsService,
});

// Initial Patient Visit
const navigatorRepository = NavigatorRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const navigatorService = NavigatorServiceImplFactory({
  navigatorRepository,
});

export const navigatorController = NavigatorControllerFactory({
  navigatorService,
});

// Compassus Navigator
const compassusNavigatorRepository = CompassusNavigatorRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const compassusNavigatorService = CompassusNavigatorServiceImplFactory({
  compassusNavigatorRepository,
});

export const compassusNavigatorController = CompassusNavigatorControllerFactory(
  {
    compassusNavigatorService,
  }
);

// Compassus Care Plan
const compassusCarePlanRepository = CompassusCarePlanRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const compassusCarePlanService = CompassusCarePlanServiceImplFactory({
  compassusCarePlanRepository,
});

export const compassusCarePlanController = CompassusCarePlanControllerFactory({
  compassusCarePlanService,
});

// AppPerformance Survey
const appPerformanceSurveyRepository = AppPerformanceSurveyRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const appPerformanceSurveyService = AppPerformanceSurveyServiceImplFactory({
  appPerformanceSurveyRepository,
});

export const appPerformanceSurveyController =
  AppPerformanceSurveyControllerFactory({
    appPerformanceSurveyService,
  });

// Address validation (Python `usaddress` running in Pyodide/WASM)
const usaddressProvider = UsaddressProviderFactory({
  createWorker: createUsaddressWorker,
});

const addressValidationService = AddressValidationServiceImplFactory({
  usaddressProvider,
});

export const addressValidationController = AddressValidationControllerFactory({
  addressValidationService,
});

const facilityRepository = FacilityRepositoryFactory({
  APIProvider: APIProvider,
  AuthService: AuthService,
  APIRoutes,
});

const facilityService = FacilityServiceImplFactory({
  facilityRepository,
});

export const facilityController = FacilityControllerFactory({
  facilityService,
});

// HealthScribe Components
export const healthScribeWebSocketProvider =
  new HealthScribeWebSocketProvider();
export const healthScribeAudioProvider = new HealthScribeAudioProvider();

const healthScribeRepository = HealthScribeRepositoryFactory({
  apiProvider: APIProvider,
  authService: authController,
});

const healthScribeService = HealthScribeServiceFactory({
  healthScribeRepository,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
});

export const healthScribeController = HealthScribeControllerFactory({
  healthScribeService,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  analyticsController: analyticsController,
});

// Telehealth
const telehealthRepository = TelehealthRepositoryFactory({
  apiProvider: APIProvider,
});

const telehealthService = TelehealthServiceImplFactory({
  telehealthRepository,
});

export const telehealthController = TelehealthControllerFactory({
  telehealthService,
});

// HPI Template
const hpiTemplateRepository = HPITemplateRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const hpiTemplateService = HPITemplateServiceImplFactory({
  hpiTemplateRepository,
});

export const hpiTemplateController = HPITemplateControllerFactory({
  hpiTemplateService,
});

// Visit Notes (Unified API for NP Visit and At Risk Review)
const visitNotesRepository = VisitNotesRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const visitNotesService = VisitNotesServiceImplFactory({
  visitNotesRepository,
});

export const visitNotesController = VisitNotesControllerFactory({
  visitNotesService,
});

// HH-RPM After-Visit Summary
const afterVisitSummaryRepository = AfterVisitSummaryRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
  ApiOrigin: apiOrigin,
});

const afterVisitSummaryService = AfterVisitSummaryServiceImplFactory({
  afterVisitSummaryRepository,
});

export const afterVisitSummaryController = AfterVisitSummaryControllerFactory({
  afterVisitSummaryService,
});

// EHR Records (Medications, Nutrition, Assessments, Allergies)
const ehrRecordsRepository = EhrRecordsRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const ehrRecordsService = EhrRecordsServiceImplFactory({
  ehrRecordsRepository,
});

export const ehrRecordsController = EhrRecordsControllerFactory({
  ehrRecordsService,
});

// Column Preferences
const columnPreferencesRepository = ColumnPreferencesRepositoryFactory({
  APIProvider,
  APIRoutes,
});

const columnPreferencesService = ColumnPreferencesServiceImplFactory({
  columnPreferencesRepository,
});

export const columnPreferencesController = ColumnPreferencesControllerFactory({
  columnPreferencesService,
});

// Feature Tours
const featureTourRepository = FeatureTourRepositoryFactory({
  APIProvider,
  APIRoutes,
});

const featureTourService = FeatureTourServiceImplFactory({
  featureTourRepository,
});

export const featureTourController = FeatureTourControllerFactory({
  featureTourService,
});

// Coordination Notes
const coordNotesRepository = CoordNotesRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const coordNotesService = CoordNotesServiceImplFactory({
  coordNotesRepository,
});

export const coordNotesController = CoordNotesControllerFactory({
  coordNotesService,
});

// HCHB coordination notes (pushed to Homecare Homebase via the CNS Gateway) —
// distinct from the local-only coordination notes above that back comments.
const hchbCoordinationNotesRepository = HchbCoordinationNotesRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const hchbCoordinationNotesService = HchbCoordinationNotesServiceImplFactory({
  hchbCoordinationNotesRepository,
});

export const hchbCoordinationNotesController =
  HchbCoordinationNotesControllerFactory({
    hchbCoordinationNotesService,
  });

// Microsoft Graph API utilities (for testing/debugging)
// Exposes window.microsoftGraph for browser console testing
import "./util/microsoftGraphApi";

// MSAL Provider for Microsoft Graph API authentication
// Exposes window.msal for browser console testing
import "./hexagon/adapters/driven/providers/msalProvider";
